import { fn } from '@ember/helper';
import { action } from '@ember/object';
import type { Registry as Services } from '@ember/service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton } from '@mylighthouse/prism-ember';
import { t, tKey } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import GeneralAccountSelectionCard from 'lumen/components/general/account-selection-card';
import type { AccountSelectorSubscription } from 'lumen/models';
import type { AccountType } from 'lumen/services/navigation';
import {
  ROUTE_LABEL_MAP,
  type RouteLabelKey,
} from 'lumen/utils/constants/navigation';

import styles from './account-selection-page.module.css';

interface HotelAccountSelectionPageSignature {
  Args: {
    onContinue: (subscriptionId: string) => void;
    redirectTo: string | null;
  };
}

const BUTTON_LABEL_KEYS = {
  brand: tKey('navigation.account_selection.go_to_brand_settings'),
  business: tKey('navigation.account_selection.go_to_management_settings'),
  chain: tKey('navigation.account_selection.go_to_chain_settings'),
  hotel: tKey('navigation.account_selection.go_to_hotel_settings'),
} as const satisfies Record<AccountType, string>;

export default class HotelAccountSelectionPage extends Component<HotelAccountSelectionPageSignature> {
  @service declare intl: Services['intl'];
  @service declare navigation: Services['navigation'];

  @tracked selectedSubscriptionId: string | undefined = undefined;

  subLabelFor = (
    subscription: AccountSelectorSubscription,
  ): string | undefined => {
    if (this.navigation.hasDuplicateTypes(this.subscriptions)) {
      return this.intl.t('general.subscription_id', { id: subscription.id });
    }

    return undefined;
  };

  get activeAccountType(): AccountType | undefined {
    const { activeSubscriptionId } = this;

    const activeSubscription = this.subscriptions.find(({ id }) => {
      return id === activeSubscriptionId;
    });

    return activeSubscription?.type;
  }

  get activeSubscriptionId(): string | undefined {
    if (this.selectedSubscriptionId) {
      return this.selectedSubscriptionId;
    }

    const currentSubscriptionId = this.navigation.currentSubscription?.id;

    if (
      currentSubscriptionId &&
      this.subscriptions.some((sub) => sub.id === currentSubscriptionId)
    ) {
      return currentSubscriptionId;
    }

    return this.subscriptions[0]?.id ?? undefined;
  }

  get buttonLabel(): string {
    return this.intl.t(BUTTON_LABEL_KEYS[this.activeAccountType!]);
  }

  get productName(): string {
    const redirectTo = this.args.redirectTo;

    if (redirectTo && redirectTo in ROUTE_LABEL_MAP) {
      return this.intl.t(ROUTE_LABEL_MAP[redirectTo as RouteLabelKey]);
    }

    return '';
  }

  get subscriptions(): AccountSelectorSubscription[] {
    const routeName = this.args.redirectTo;

    if (routeName) {
      return this.navigation.getSubscriptionsForRoute(routeName);
    }

    return this.navigation.accountSelectorSubscriptions;
  }

  @action
  handleContinue(): void {
    if (this.activeSubscriptionId) {
      this.args.onContinue(this.activeSubscriptionId);
    }
  }

  @action
  selectSubscription(subscriptionId: string): void {
    this.selectedSubscriptionId = subscriptionId;
  }

  <template>
    <div
      class="flex h-full items-center justify-center text-center"
      data-test-id="account-selection-page"
    >
      <div class={{styles.content}}>
        <div class="flex flex-col gap-200">
          <h3>
            {{t "navigation.account_selection.title" product=this.productName}}
          </h3>
          <p class="paragraph-300--regular">
            {{t "navigation.account_selection.description"}}
          </p>
        </div>

        <div class={{styles.grid}}>
          {{#each this.subscriptions as |subscription|}}
            <GeneralAccountSelectionCard
              @accountType={{subscription.type}}
              @isSelected={{eq this.activeSubscriptionId subscription.id}}
              @onClick={{fn this.selectSubscription subscription.id}}
              @subLabel={{this.subLabelFor subscription}}
            />
          {{/each}}
        </div>

        <PrismButton
          @buttonType="primary"
          @label={{this.buttonLabel}}
          @onClick={{this.handleContinue}}
          data-test-id="account-selection-continue-button"
        />
      </div>
    </div>
  </template>
}
